import React from 'react';
import KanjiLookup from '../components/KanjiLookup';
import ChineseLookup from '../components/ChineseLookup';
import TinySegmenter from './tinySegmenter';
import logger from './logger';

/**
 * Splits a word into alternating CJK and non-CJK segments.
 * This separates Chinese characters from attached punctuation so that
 * punctuation can be rendered as plain text instead of being bundled
 * into the ChineseLookup component (where it can appear "invisible").
 *
 * Example: "中文。" → [{text:"中文", isCJK:true}, {text:"。", isCJK:false}]
 * Example: "“你好" → [{text:"“", isCJK:false}, {text:"你好", isCJK:true}]
 */
function splitChineseWord(word) {
    const segments = [];
    let current = '';
    let currentIsCJK = null;

    for (const char of word) {
        const isCJK = /[\u4E00-\u9FFF]/.test(char);
        if (currentIsCJK === null) {
            currentIsCJK = isCJK;
            current = char;
        } else if (isCJK === currentIsCJK) {
            current += char;
        } else {
            segments.push({ text: current, isCJK: currentIsCJK });
            currentIsCJK = isCJK;
            current = char;
        }
    }
    if (current) {
        segments.push({ text: current, isCJK: currentIsCJK });
    }
    return segments;
}

// Accept a lookupKey to ensure keys are globally unique across different sentences
const processSentenceJisho = (input, lookupKey = '') => {
    let sentence = input;
    if (typeof sentence !== 'string') {
        if (sentence && typeof sentence.targetSentence === 'string') {
            sentence = sentence.targetSentence;
        } else {
            return sentence;
        }
    }

    // If there are no Japanese or Chinese characters, return the sentence as-is.
    if (!/[\u4E00-\u9FFF\u3040-\u309F\u30A0-\u30FF]/.test(sentence)) {
        return sentence;
    }

    // Check if the text contains Chinese characters without any Japanese-specific characters
    const containsJapaneseChars = /[\u3040-\u309F\u30A0-\u30FF]/.test(sentence);
    const containsChineseOnly = /[\u4E00-\u9FFF]/.test(sentence) && !containsJapaneseChars;

    let words;
    // For Chinese text with spaces (Gemini-segmented), strip spaces for the context sentence
    let contextSentence = sentence;

    if (containsChineseOnly) {
        // For Chinese text, check if Gemini has already separated words with spaces
        // (new sentences generated with the updated prompt in sentenceService.js)
        if (/\s/.test(sentence)) {
            // Split by spaces to get proper word boundaries from Gemini
            words = sentence.split(/\s+/).filter(Boolean);
            // Remove spaces to create a clean context sentence for Lookup components
            contextSentence = sentence.replace(/\s+/g, '');
            logger.debug(`processSentenceJisho - Chinese text with space-separated words:`, words);
        } else {
            // Fallback for older sentences without spaces: use TinySegmenter
            // (imperfect for Chinese, but maintains backward compatibility with existing data)
            const segmenter = new TinySegmenter();
            words = segmenter.segment(sentence);
            logger.debug(`processSentenceJisho - Chinese text without spaces, using TinySegmenter fallback`);
        }
    } else {
        // For Japanese, use TinySegmenter as before
        const segmenter = new TinySegmenter();
        words = segmenter.segment(sentence);
    }

    // Create a container element to avoid adding spaces between elements
    // When words come from a space-delimited source (Gemini), the array map
    // naturally renders without visible gaps between word elements
    return (
        <span className={containsChineseOnly ? "chinese-sentence" : "japanese-sentence"}>
            {words.map((word, index) => {
                if (/[\u4E00-\u9FFF\u3040-\u309F\u30A0-\u30FF]/.test(word)) {
                    // Use ChineseLookup only for Chinese text without Japanese characters
                    if (containsChineseOnly) {
                        // Split word into CJK and non-CJK segments to separate
                        // punctuation from Chinese characters (e.g., "中文。" → "中文" + "。")
                        const segments = splitChineseWord(word);

                        // If word is purely CJK with no attached non-CJK, use ChineseLookup directly
                        if (segments.length === 1 && segments[0].isCJK) {
                            return (
                                <ChineseLookup
                                    key={`${lookupKey}-hanzi-${index}-${word}`}
                                    word={word}
                                    sentence={contextSentence}
                                >
                                    {word}
                                </ChineseLookup>
                            );
                        }

                        // Mixed segments: render CJK parts as ChineseLookup, non-CJK as plain text
                        return (
                            <React.Fragment key={`${lookupKey}-hanzi-${index}-${word}`}>
                                {segments.map((seg, segIdx) =>
                                    seg.isCJK ? (
                                        <ChineseLookup
                                            key={`seg-${segIdx}`}
                                            word={seg.text}
                                            sentence={contextSentence}
                                        >
                                            {seg.text}
                                        </ChineseLookup>
                                    ) : (
                                        <span key={`seg-${segIdx}`} style={{ margin: 0, padding: 0 }}>
                                            {seg.text}
                                        </span>
                                    )
                                )}
                            </React.Fragment>
                        );
                    } else {
                        return (
                            <KanjiLookup
                                /* Include lookupKey and word in the key to prevent component reuse */
                                key={`${lookupKey}-kanji-${index}-${word}`}
                                word={word}
                                sentence={sentence}
                            >
                                {word}
                            </KanjiLookup>
                        );
                    }
                }
                return <React.Fragment key={`${lookupKey}-text-${index}-${word}`}>{word}</React.Fragment>;
            })}
        </span>
    );
};

export default processSentenceJisho;
